import {IApiResponse} from "./util";
import {fetch} from "../../services/fetch";


export type TApiResponseChangeListener = (newResponse: Readonly<IApiResponse>) => void;


const cacheKey = 'language-chooser-cache';
const apiCalls = [];
const apiResponses = new Map<string, Readonly<IApiResponse>>();
const apiResponseChangeListeners = new Map<string, Set<TApiResponseChangeListener>>();

/**
 * Add a listener for changes to the API response on a particular URL
 * @param url
 * @param listener
 */
export function addApiResponseChangeListener(url: URL, listener: TApiResponseChangeListener) {
    let listeners = apiResponseChangeListeners.get(url.href);

    if (!listeners) {
        listeners = new Set();
        apiResponseChangeListeners.set(url.href, listeners);
    }

    listeners.add(listener);
}

/**
 * Fetch the API response for a particular API
 * @todo type-check the result
 * @param url
 */
async function fetchApiResponse(url: URL): Promise<IApiResponse> {
    const response = await fetch(url.href);

    if (response.ok) {
        return await response.json();
    } else {
        throw new Error('Error fetching API response!');
    }
}

/**
 * Get the API response for a particular URL from the cache.
 * Also starts the refresh process, if necessary
 * @param url
 */
export function getApiResponse(url: URL): IApiResponse | undefined {
    let response = apiResponses.get(url.href);

    if (!response) {
        response = loadFromCache(url);
        apiResponses.set(url.href, response);
    }

    // retrieve fresh api result
    if (!apiCalls.includes(url.href)) {
        apiCalls.push(url.href);

        (async () => {
            const newResponse = Object.freeze(await fetchApiResponse(url));

            // only update if different
            if (JSON.stringify(apiResponses.get(url.href)) != JSON.stringify(newResponse)) {
                apiResponses.set(url.href, newResponse);
                saveToCache(url, newResponse);
                (apiResponseChangeListeners.get(url.href) ?? new Set())
                    .forEach(listener => listener(Object.freeze(Object.assign({}, newResponse))));
            }
        })();
    }

    return response;
}

/**
 * Load cached API response for a particular URL
 * @param url
 */
function loadFromCache(url: URL): IApiResponse | undefined {
    return loadResponsesFromCache()[url.href];
}

/**
 * Load all cached API responses
 */
function loadResponsesFromCache(): Record<string, IApiResponse> {
    const cache = localStorage.getItem(cacheKey) ?? '{}';

    try {
        return JSON.parse(cache);
    } catch (e) {
        console.error('dc-language-chooser: Corrupted cache', e);
    }
}

/**
 * Remove a change listener
 * @param url
 * @param listener
 */
export function removeApiResponseChangeListener(url: URL, listener: TApiResponseChangeListener) {
    let listeners = apiResponseChangeListeners.get(url.href);

    if (!listeners) {
        listeners = new Set();
        apiResponseChangeListeners.set(url.href, listeners);
    }

    listeners.delete(listener);
}

/**
 * Save an API response to the cache
 * @param url
 * @param response
 */
function saveToCache(url: URL, response: IApiResponse) {
    const responses = loadResponsesFromCache();

    responses[url.href] = response;
    localStorage.setItem(cacheKey, JSON.stringify(responses));
}
